/**
 * Single source of truth for supported languages in the seller portal.
 * Add a locale here only — lists, bundles, flags, and defaults follow.
 */

import i18n from "i18next";
import countryNamesEn from "react-phone-number-input/locale/en.json";
import countryNamesEs from "react-phone-number-input/locale/es.json";
import countryNamesIt from "react-phone-number-input/locale/it.json";
import countryNamesDe from "react-phone-number-input/locale/de.json";
import type { CountryCode } from "@/lib/phone/countryCode";
import en from "@/i18n/locales/en";
import es from "@/i18n/locales/es";
import it from "@/i18n/locales/it";
import de from "@/i18n/locales/de";

const LOCALES = {
  en: {
    flag: "🇬🇧",
    intlTag: "en-GB",
    phoneCountry: "GB" as const satisfies CountryCode,
    i18n: en,
  },
  es: {
    flag: "🇪🇸",
    intlTag: "es-ES",
    phoneCountry: "ES" as const satisfies CountryCode,
    i18n: es,
  },
  it: {
    flag: "🇮🇹",
    intlTag: "it-IT",
    phoneCountry: "IT" as const satisfies CountryCode,
    i18n: it,
  },
  de: {
    flag: "🇩🇪",
    intlTag: "de-DE",
    phoneCountry: "DE" as const satisfies CountryCode,
    i18n: de,
  },
};

const COUNTRY_NAMES = {
  en: countryNamesEn,
  es: countryNamesEs,
  it: countryNamesIt,
  de: countryNamesDe,
} as const;

export type SupportedLocale = keyof typeof LOCALES;

export const SUPPORTED_LOCALES = Object.keys(LOCALES) as [
  SupportedLocale,
  ...SupportedLocale[],
];

export const DEFAULT_LOCALE = "en" satisfies SupportedLocale;

/** Global phone-country fallback — always aligned with `DEFAULT_LOCALE`. */
export const DEFAULT_PHONE_COUNTRY: CountryCode =
  LOCALES[DEFAULT_LOCALE].phoneCountry;

/** Same key tree as `en`; leaves are `string` so translations are not forced to English literals. */
type DeepStringify<T> = T extends string
  ? string
  : { [K in keyof T]: DeepStringify<T[K]> };

type LocaleMessages = DeepStringify<typeof en>;

/** i18next resource bundles — missing keys/namespaces fail the build. */
export const I18N_RESOURCES = {
  en,
  es,
  it,
  de,
} satisfies Record<SupportedLocale, LocaleMessages>;

export function isSupportedLocale(code: string): code is SupportedLocale {
  return code in LOCALES;
}

export function resolveLocale(raw?: string | null): SupportedLocale {
  const base = (raw ?? DEFAULT_LOCALE).split("-")[0].toLowerCase();
  return isSupportedLocale(base) ? base : DEFAULT_LOCALE;
}

/** BCP 47 tag for `Intl` formatters from a supported locale code. */
export function getIntlLocaleTag(locale?: SupportedLocale): string {
  return LOCALES[locale ?? DEFAULT_LOCALE].intlTag;
}

export function defaultPhoneCountryForLanguage(
  language?: string | null,
): CountryCode {
  return LOCALES[resolveLocale(language)].phoneCountry;
}

/** Localized country label from ISO code (react-phone-number-input locale data). */
export function getCountryName(
  country: CountryCode | string,
  language: string,
): string {
  const locale = resolveLocale(language);
  const names = COUNTRY_NAMES[locale];
  return names[country as keyof typeof names] || String(country);
}

export function localeFlag(locale: SupportedLocale): string {
  return LOCALES[locale].flag;
}

/** Localized language name from i18n `constants.lang.*`. */
export function supportedLanguageLabel(code: string): string {
  return i18n.t(`lang.${code}`, { ns: "constants" });
}

/** Flag + localized language name for selects and toggles. */
export function supportedLanguageSelectLabel(code: string): string {
  if (isSupportedLocale(code)) {
    return `${localeFlag(code)} ${supportedLanguageLabel(code)}`;
  }
  return supportedLanguageLabel(code);
}

export function supportedLocaleSelectOptions(): {
  value: SupportedLocale;
  label: string;
}[] {
  return SUPPORTED_LOCALES.map((code) => ({
    value: code,
    label: supportedLanguageSelectLabel(code),
  }));
}
